<template>
    <v-container fluid>
        <v-row class="mx-0">
            <v-col cols="12" md="5" class="pt-0 px-sm-0">
                <v-skeleton-loader
                    :loading="$apollo.queries.growthTask.loading"
                    type="article, card-heading, card-heading, card-heading, card-heading"
                >
                    <GrowthTaskCard
                        :task="growthTask"
                        v-on:taskSplited="onTaskSplited"
                        v-on:taskDeleted="onTaskDeleted"
                        v-on:growthTaskPhasesUpdated="growthTaskPhasesUpdated"
                        v-on:growthTaskPhaseConfirmed="growthTaskPhaseConfirmed"
                        v-on:growthTaskInventoryUpdated="
                            growthTaskInventoryUpdated
                        "
                    ></GrowthTaskCard>
                </v-skeleton-loader>
            </v-col>

            <!-- Timeline -->

            <v-col cols="12" md="7">
                <v-card
                    flat
                    class="grey lighten-5"
                    :loading="$apollo.queries.growthTask.loading"
                >
                    <v-card-title class="ml-3 headline"
                        >Inventory history</v-card-title
                    >

                    <v-card-text
                        v-if="growthTask.growthTaskInventoryLogSet"
                        class="pa-0"
                    >
                        <v-timeline
                            align-top
                            dense
                            clipped
                            v-for="log in growthTask.growthTaskInventoryLogSet
                                .edges"
                            :key="log.node.id"
                        >
                            <v-timeline-item color="primary" small>
                                <v-row class="mt-n2">
                                    <v-col cols="6">
                                        <div>
                                            <strong v-if="log.node.change > 0"
                                                >Change: +{{
                                                    log.node.change
                                                }}</strong
                                            >
                                            <strong v-else
                                                >Change:
                                                {{ log.node.change }}</strong
                                            >
                                        </div>
                                        <div class="caption">
                                            {{
                                                log.node.createdAt
                                                    | moment("from", new Date())
                                            }}
                                        </div>
                                        <div class="caption">
                                            {{ log.node.updatedBy }}
                                        </div>
                                    </v-col>
                                    <v-col cols="6">
                                        <strong
                                            >Inventory:
                                            {{
                                                log.node.inventoryAfterChange
                                            }}</strong
                                        >
                                        <div class="body-2 align-center">
                                            <v-chip small label>
                                                {{ log.node.changeReason }}
                                            </v-chip>
                                        </div>
                                        <v-list-item-subtitle
                                            v-if="log.node.changeReasonTag"
                                        >
                                            {{ log.node.changeReasonTag.tag }}
                                        </v-list-item-subtitle>
                                        <v-list-item-subtitle
                                            class="text-wrap"
                                            v-if="
                                                log.node.changeReason !==
                                                    'SPLIT'
                                            "
                                        >
                                            {{ log.node.comment }}
                                        </v-list-item-subtitle>
                                    </v-col>
                                </v-row>
                            </v-timeline-item>
                        </v-timeline>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>


<script>
import gql from "graphql-tag";
import GrowthTaskCard from "@/components/growth/GrowthTaskCard.vue";

export default {
    name: "growth-task-detail-component",
    props: ["growthTaskNodeId"],
    components: { GrowthTaskCard },

    apollo: {
        growthTask: {
            query: gql`
                query growthTask($id: ID!) {
                    growthTask(id: $id) {
                        id
                        rawId
                        note
                        numPhases
                        plant {
                            id
                            name
                            plantSpecies {
                                id
                                name
                            }
                            plantTagSet {
                                edges {
                                    node {
                                        id
                                        tag {
                                            id
                                            tag
                                            orderId
                                        }
                                    }
                                }
                            }
                        }
                        color {
                            id
                            color
                        }
                        potSize {
                            id
                            potSizeCm
                        }
                        environment {
                            id
                            environment
                        }
                        tag {
                            id
                            tag
                        }
                        plantGrowthTemplate {
                            id
                            name
                        }
                        supplier {
                            id
                            name
                        }
                        totalGrowthDays
                        maxSalesDays
                        growthStartDate
                        growthEndDate
                        salesStartDate
                        salesEndDate
                        lockedDate
                        lockedComment
                        lockedForCustomer {
                            id
                            name
                        }
                        closedDate
                        closedReason
                        supplierOrderNo
                        totalQuantity
                        totalInventory
                        unitPurchasePrice
                        currency
                        growthTaskPhaseSet {
                            edges {
                                node {
                                    id
                                    phaseNo
                                    startDate
                                    endDate
                                    numDays
                                    numPlantsPerM2
                                    spaceM2
                                    isSalesPhase
                                    isCurrentPhase
                                    remindedDate
                                    confirmedDate
                                    greenhouseDepartmentSection {
                                        id
                                        name
                                        greenhouseDepartment {
                                            id
                                            name
                                        }
                                    }
                                }
                            }
                        }
                        growthTaskInventoryLogSet(orderBy: "-createdAt") {
                            edges {
                                node {
                                    id
                                    createdAt
                                    updatedBy
                                    change
                                    changeReason
                                    changeReasonTag {
                                        id
                                        tag
                                    }
                                    inventoryAfterChange
                                    refType
                                    refId
                                    comment
                                }
                            }
                        }
                        createdAt
                        updatedAt
                    }
                }
            `,
            variables() {
                return {
                    id: this.growthTaskNodeId
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update(data) {
                //send data to parent component
                this.$emit("item-fetched", data.growthTask);

                if (!data.growthTask) {
                    alert("Unknown growth task!");
                    this.$emit("unknown");
                }

                //return result to current componet
                return data.growthTask;
                // return JSON.parse(JSON.stringify(data.growthTask));
            },
            pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            isEditingProductItem: false,
            isSavingProductItem: false,
            editProductDialog: false,
            deleteProductDialog: false,

            growthTask: {
                id: null,
                name: "",
                isDynamic: false,
                salesPrice: null,
                currency: "EUR",
                growthTaskCategory: {
                    id: null,
                    name: ""
                },
                growthTaskItemSet: {
                    edges: []
                }
            }
        };
    },
    computed: {
        growthTaskItemNumPlantsCount() {
            return this.growthTask.growthTaskItemSet.edges
                .map(item => item.node.numPlants)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0
                );
        },
        distinctedPlantColors() {
            return [
                ...new Set(
                    this.growthTask.growthTaskItemSet.edges.map(
                        item => item.node.color.id
                    )
                )
            ];
        }
    },
    watch: {},
    created() {},
    methods: {
        growthTaskPhasesUpdated() {
            this.$apollo.queries.growthTask.refresh();
        },

        growthTaskInventoryUpdated() {
            this.$apollo.queries.growthTask.refresh();
        },

        growthTaskPhaseConfirmed() {
            this.$apollo.queries.growthTask.refresh();
        },

        onTaskSplited() {
            this.$apollo.queries.growthTask.refresh();
        },

        onTaskDeleted() {
            // this.$apollo.queries.growthTask.refresh();
            this.$emit("item-deleted");
        }
    }
};
</script>